import * as React from 'react';

import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  'focus-visible:ring-ring inline-flex items-center justify-center rounded-md text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-1 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground border-2 border-[#303030] shadow-[inset_1px_1px_0_0_#303030,3px_3px_0_0_#303030] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[inset_1px_1px_0_0_#303030,2px_2px_0_0_#303030] dark:border-neutral-100 dark:shadow-[2px_2px_0_0_#f5f5f5] dark:hover:shadow-[1px_1px_0_0_#f5f5f5]',
        destructive:
          'bg-destructive text-destructive-foreground border-2 border-red-900 shadow-[2px_2px_0_0_#7f1d1d] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0_0_#7f1d1d] dark:border-red-400 dark:shadow-[2px_2px_0_0_#f87171] dark:hover:shadow-[1px_1px_0_0_#f87171]',
        outline:
          'bg-background hover:bg-accent hover:text-accent-foreground border-2 border-neutral-900 shadow-[2px_2px_0_0_#171717] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0_0_#171717] dark:border-neutral-100 dark:shadow-[2px_2px_0_0_#f5f5f5] dark:hover:shadow-[1px_1px_0_0_#f5f5f5]',
        secondary:
          'bg-secondary text-secondary-foreground border-2 border-neutral-400 shadow-[2px_2px_0_0_#a3a3a3] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[1px_1px_0_0_#a3a3a3] dark:border-neutral-600 dark:shadow-[2px_2px_0_0_#525252] dark:hover:shadow-[1px_1px_0_0_#525252]',
        // Soft: Light tinted background, no shadow - subtle but visible
        soft: 'bg-primary/15 text-primary border-primary/30 hover:bg-primary/25 hover:border-primary/40 border-2',
        // Muted: Even more subtle, minimal border
        muted:
          'bg-primary/10 text-primary/80 border-primary/20 hover:bg-primary/20 hover:text-primary border',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'decoration-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        xl: 'h-12 rounded-lg px-6 py-3 text-base',
        '2xl': 'rounded-lg px-8 py-4 text-xl',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends
    React.ComponentPropsWithRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  className,
  variant,
  size,
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
};

Button.displayName = 'Button';

export { Button, buttonVariants };
